/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Mask Compositing Node
   Graph-native alpha & luminance masking with invert & strength controls.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_COLOR_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getTextureFromMedia } from '../../rendering/media';

const MASK_SHADER = `${GLSL_HEADER}
${GLSL_COLOR_UTILS}

uniform sampler2D u_image;
uniform sampler2D u_mask;
uniform bool u_hasMask;
uniform int u_mode; // 0 = Luminance, 1 = Alpha
uniform bool u_invert;
uniform float u_strength; // 0.0 .. 1.0

void main() {
  vec4 src = texture(u_image, v_uv);

  if (!u_hasMask) {
    fragColor = src;
    return;
  }

  vec4 maskCol = texture(u_mask, v_uv);
  
  float maskVal = 1.0;
  if (u_mode == 0) {
    // Luminance mask
    maskVal = getLuminance(maskCol.rgb) * maskCol.a;
  } else {
    // Alpha channel mask
    maskVal = maskCol.a;
  }

  if (u_invert) {
    maskVal = 1.0 - maskVal;
  }

  // Modulate alpha based on mask and strength
  float finalAlpha = src.a * mix(1.0, maskVal, u_strength);

  fragColor = vec4(src.rgb, clamp(finalAlpha, 0.0, 1.0));
}
`;

export const maskNode: NodeDefinition = {
  type: 'composite.mask',
  category: 'Composite',
  label: 'Mask',
  description: 'Masks image transparency using another image’s luminance or alpha channel',
  keywords: ['mask', 'matte', 'alpha', 'luminance', 'cutout', 'isolate', 'stencil', 'transparency'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source Image', type: 'image' },
    { id: 'mask', name: 'Mask Image', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Masked', type: 'image' },
  ],
  params: [
    {
      id: 'mode',
      name: 'Mask Mode',
      type: 'select',
      defaultValue: 'luminance',
      options: [
        { label: 'Luminance (Grayscale)', value: 'luminance' },
        { label: 'Alpha Channel', value: 'alpha' },
      ],
    },
    {
      id: 'invert',
      name: 'Invert Mask',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
    {
      id: 'strength',
      name: 'Mask Strength',
      type: 'slider',
      defaultValue: 100,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: MASK_SHADER,
    primaryInputPort: 'image',
    getUniforms: (inputs, params) => {
      const maskTex = getTextureFromMedia(inputs.mask);
      const hasMask = Boolean(maskTex && maskTex.nativeHandle);

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_mask: hasMask ? (maskTex as import('../../rendering/types').TextureHandle) : (inputs.image as unknown as import('../../rendering/types').TextureHandle),
        u_hasMask: hasMask,
        u_mode: params.mode === 'alpha' ? 1 : 0,
        u_invert: Boolean(params.invert),
        u_strength: Number(params.strength ?? 100) / 100,
      };
    },
  }),
};
